/**
 * ai-agent-simple
 *
 * Modular AI framework for text generation, TTS, and more
 */

// Core exports
export * from './core/types';
export { BaseProvider } from './core/base-provider';
export { AgentController } from './core/agent-controller';
export { ProviderManager } from './core/provider-manager';

// Text providers
export { OpenAIText } from './providers/text/openai';
export { AlkahestText } from './providers/text/alkahest';
export { MahoText } from './providers/text/maho';
export { AkashText } from './providers/text/akash';

// TTS providers
export { OpenAITTS } from './providers/tts/openai-tts';
export { AlkahestTTS } from './providers/tts/alkahest-tts';

// Image providers
export { AlkahestImage } from './providers/image/alkahest';

// Services
export { ConversationService } from './services/conversation';
export { TipManagementService } from './services/tip-management';
export { AudioControlService } from './services/audio-control';
export { VRMIntegrationService } from './services/vrm-integration';
export { CanvasIntegrationService } from './services/canvas-integration';

// Adapters
export { PartyKitAdapter } from './adapters/partykit';

// Main framework class
import {
  AIProvider,
  ProviderType,
  ProviderConfig,
  PersonalityProfile,
  AIResponse,
} from './core/types';
import { OpenAIText } from './providers/text/openai';
import { AlkahestText } from './providers/text/alkahest';
import { MahoText } from './providers/text/maho';
import { AkashText } from './providers/text/akash';
import { OpenAITTS } from './providers/tts/openai-tts';
import { AlkahestTTS } from './providers/tts/alkahest-tts';
import { AlkahestImage } from './providers/image/alkahest';
import { ConversationService } from './services/conversation';
import { PartyKitAdapter } from './adapters/partykit';

export interface AIFrameworkConfig {
  providers?: {
    text?: ProviderConfig;
    tts?: ProviderConfig;
    image?: ProviderConfig;
  };
  conversation?: {
    enableTTS?: boolean;
    enableActions?: boolean;
    maxContextMessages?: number;
  };
}

export class AIFramework {
  private providers = new Map<ProviderType, AIProvider>();
  private conversationService?: ConversationService;
  private partyKitAdapter?: PartyKitAdapter;

  constructor(private config: AIFrameworkConfig = {}) {}

  async initialize(): Promise<void> {
    // Initialize text provider
    if (this.config.providers?.text) {
      const providerType = this.config.providers.text.provider || 'openai';
      let textProvider;

      switch (providerType) {
        case 'alkahest':
          textProvider = new AlkahestText();
          break;
        case 'maho':
          textProvider = new MahoText();
          break;
        case 'akash':
          textProvider = new AkashText();
          break;
        case 'openai':
        default:
          textProvider = new OpenAIText();
          break;
      }

      await textProvider.initialize(this.config.providers.text);
      this.providers.set('text', textProvider);
    }

    // Initialize TTS provider
    if (this.config.providers?.tts) {
      const providerType = this.config.providers.tts.provider || 'openai';
      let ttsProvider;

      switch (providerType) {
        case 'alkahest':
          ttsProvider = new AlkahestTTS();
          break;
        case 'openai':
        default:
          ttsProvider = new OpenAITTS();
          break;
      }

      await ttsProvider.initialize(this.config.providers.tts);
      this.providers.set('tts', ttsProvider);
    }

    // Initialize image provider
    if (this.config.providers?.image) {
      const providerType = this.config.providers.image.provider || 'alkahest';
      let imageProvider;

      switch (providerType) {
        case 'alkahest':
          imageProvider = new AlkahestImage();
          break;
        default:
          throw new Error(`Unsupported image provider: ${providerType}`);
      }

      await imageProvider.initialize(this.config.providers.image);
      this.providers.set('image', imageProvider);
    }

    // Initialize conversation service
    const textProvider = this.providers.get('text') as any;
    const ttsProvider = this.providers.get('tts') as any;

    if (textProvider) {
      this.conversationService = new ConversationService(
        textProvider,
        ttsProvider,
        this.config.conversation
      );

      this.partyKitAdapter = new PartyKitAdapter(this.conversationService);
    }
  }

  getProvider(type: ProviderType): AIProvider | undefined {
    return this.providers.get(type);
  }

  get conversation(): ConversationService {
    if (!this.conversationService) {
      throw new Error('Conversation service not initialized');
    }
    return this.conversationService;
  }

  get partyKit(): PartyKitAdapter {
    if (!this.partyKitAdapter) {
      throw new Error('PartyKit adapter not initialized');
    }
    return this.partyKitAdapter;
  }

  async processMessage(
    userId: string,
    message: string,
    personality?: PersonalityProfile,
    metadata?: Record<string, any>
  ): Promise<AIResponse> {
    return this.conversation.processMessage(userId, message, personality, metadata);
  }
}
